// Veteran mode (MASTER-PLAN.md §2.17). Death is enabled here and nowhere
// else, so it has to be honest: birds die on the timescales the veterinary
// literature actually reports, from causes a real keeper would recognise.
//
// Survival constants come from clay-farm-science 07-physiology-limits
// (PHY-06, PHY-08, PHY-09, PHY-11), each backed by two cited sources.

import { clamp, round2 } from '../core/math';
import type { ChickenEntity, DeathCause, ExposureClock, GameState } from '../core/types';
import type { BiologyConfig } from './config';
import { breedProfile } from './config';
import { isSilkieFeathered } from './genetics';
import type { EnvContext } from './physiology';

export type { DeathCause, ExposureClock };

export function emptyExposure(): ExposureClock {
  return { dehydration: 0, starvation: 0, hypothermia: 0, hyperthermia: 0 };
}

/**
 * Hours without water before death, interpolated across the researched points
 * (PHY-06: 60h at 75°F, 36h at 85°F, 18h at 95°F). Heat is what makes thirst
 * lethal quickly, which is exactly the Puerto Rico lesson.
 */
export function dehydrationDeathHours(
  tempF: number,
  isChick: boolean,
  config: BiologyConfig,
): number {
  const s = config.survival;
  const points: Array<[number, number]> = [
    [75, s.dehydrationDeathHoursAt75F],
    [85, s.dehydrationDeathHoursAt85F],
    [95, s.dehydrationDeathHoursAt95F],
  ];
  let hours: number;
  if (tempF <= points[0][0]) hours = points[0][1];
  else if (tempF >= points[2][0]) hours = points[2][1];
  else {
    const [loT, loH] = tempF <= points[1][0] ? points[0] : points[1];
    const [hiT, hiH] = tempF <= points[1][0] ? points[1] : points[2];
    hours = loH + ((tempF - loT) / (hiT - loT)) * (hiH - loH);
  }
  // PHY-08: chicks dehydrate 2–3× faster than adults.
  return isChick ? hours / s.chickDehydrationMultiplier : hours;
}

export function starvationDeathHours(
  chicken: ChickenEntity,
  config: BiologyConfig,
): number {
  const s = config.survival;
  if (chicken.stage === 'chick') return s.starvationDeathHoursChick;
  if (chicken.stage === 'pullet') return s.starvationDeathHoursPullet;
  return s.starvationDeathHoursAdult;
}

/**
 * A soaked non-waterproof bird is the PR killer, not cold: wet plumage loses
 * ~87% of its insulation, so a Silkie in 68°F rain is hypothermic in hours
 * (PHY-11). Waterproof breeds shed it and are not at risk here.
 */
export function isHypothermiaRisk(
  chicken: ChickenEntity,
  env: EnvContext,
  config: BiologyConfig,
): boolean {
  if (!env.wetAndExposed) return false;
  const waterproof = breedProfile(chicken.breed).waterproof && !isSilkieFeathered(chicken.genotype);
  if (waterproof) return false;
  return env.tempF <= config.survival.wetHypothermiaAmbientF;
}

/** PHY-10: lethal above ~100°F once humidity blocks evaporative cooling. */
export function isHeatStrokeRisk(env: EnvContext, humidity: number, config: BiologyConfig): boolean {
  const t = config.temperature;
  if (env.sheltered && env.hasWater) return false;
  return env.tempF >= t.lethalAmbientF && humidity >= t.lethalHumidityPercent;
}

export interface MortalityResult {
  died: boolean;
  cause?: DeathCause;
}

/**
 * Advances a bird's exposure clocks and reports death when one runs out.
 * Called only when the profile allows death — normal mode never reaches here.
 */
export function stepMortality(
  chicken: ChickenEntity,
  env: EnvContext,
  humidity: number,
  hours: number,
  config: BiologyConfig,
): MortalityResult {
  const clock = (chicken.exposure ??= emptyExposure());
  const isChick = chicken.stage === 'chick';

  // Water and feed clocks run while the resource is missing and reset the
  // moment it is restored — a bird that drinks is out of danger, not "damaged".
  if (env.hasWater) clock.dehydration = 0;
  else clock.dehydration += hours;

  if (env.hasFeed) clock.starvation = 0;
  else clock.starvation += hours;

  if (isHypothermiaRisk(chicken, env, config)) clock.hypothermia += hours;
  else clock.hypothermia = Math.max(0, clock.hypothermia - hours * 2); // drying off is fast

  if (isHeatStrokeRisk(env, humidity, config)) clock.hyperthermia += hours;
  else clock.hyperthermia = Math.max(0, clock.hyperthermia - hours * 2);

  // Not rounded. Veteran runs at 1x Real-Time, so a tick adds 1/3600 of an
  // hour to these clocks — rounding to 2dp floored every one of them back to
  // zero before the next tick, and no bird could ever die of dehydration,
  // starvation, hypothermia or heat stroke. The only working death path left
  // was the predator night, which resolves once a night and never touches
  // these. Same accumulator-rounding bug as the six already fixed elsewhere.

  const s = config.survival;
  if (clock.dehydration >= dehydrationDeathHours(env.tempF, isChick, config)) {
    return { died: true, cause: 'dehydration' };
  }
  if (clock.starvation >= starvationDeathHours(chicken, config)) {
    return { died: true, cause: 'starvation' };
  }
  if (clock.hypothermia >= s.wetHypothermiaDeathHours) {
    return { died: true, cause: 'hypothermia' };
  }
  if (clock.hyperthermia >= s.hyperthermiaDeathHours) {
    return { died: true, cause: 'heat_stroke' };
  }
  return { died: false };
}

/** How close the bird is to its worst clock running out, 0–1. */
export function mortalityPressure(
  chicken: ChickenEntity,
  env: EnvContext,
  config: BiologyConfig,
): number {
  const clock = chicken.exposure;
  if (!clock) return 0;
  const s = config.survival;
  return clamp(
    Math.max(
      clock.dehydration / dehydrationDeathHours(env.tempF, chicken.stage === 'chick', config),
      clock.starvation / starvationDeathHours(chicken, config),
      clock.hypothermia / s.wetHypothermiaDeathHours,
      clock.hyperthermia / s.hyperthermiaDeathHours,
    ),
    0,
    1,
  );
}

// --- Scoring (MASTER-PLAN.md Phase 5.5) ---

/**
 * Score = flock-days survived × flock size × mean breed difficulty.
 * Keeping three Silkies alive is genuinely harder than keeping three Rhode
 * Island Reds alive, and the score says so.
 */
export function difficultyOf(state: GameState): number {
  const living = state.flock.filter((c) => c.alive);
  if (living.length === 0) return 0;
  const sum = living.reduce((acc, c) => acc + breedProfile(c.breed).difficultyMultiplier, 0);
  return round2(sum / living.length);
}

export function scoreFor(state: GameState): number {
  const living = state.flock.filter((c) => c.alive).length;
  if (living === 0) return state.veteran.bestScore;
  return Math.round(state.veteran.flockDaysSurvived * living * difficultyOf(state));
}

/** A run ends when the last bird dies. */
export function isRunOver(state: GameState): boolean {
  return state.veteran.enabled && state.flock.every((c) => !c.alive);
}

/**
 * Advances the run clock and banks the best score. Flock-days accrue per
 * living bird, so a bigger flock survives "more" per day — the same reason
 * the multiplier is there.
 */
export function stepVeteranRun(state: GameState, hours: number): void {
  if (!state.veteran.enabled) return;
  const living = state.flock.filter((c) => c.alive).length;
  if (living === 0) return;
  // Full precision deliberately. A real-time tick adds 1/86400 of a day, and
  // rounding to 2dp would swallow every increment and freeze the run at zero —
  // the same trap the world clock fell into. Rounding happens at display.
  state.veteran.flockDaysSurvived += hours / 24;
  state.veteran.bestScore = Math.max(state.veteran.bestScore, scoreFor(state));
}

/** Starts a fresh veteran run on the current farm. */
export function beginVeteranRun(state: GameState, nickname?: string): void {
  state.veteran = {
    enabled: true,
    unlocked: true,
    startedAtDay: Math.floor(state.simHour / 24),
    flockDaysSurvived: 0,
    bestScore: state.veteran.bestScore,
    nickname: nickname ?? state.veteran.nickname,
    actions: [],
  };
  for (const chicken of state.flock) chicken.exposure = emptyExposure();
}

export function endVeteranRun(state: GameState): number {
  const final = scoreFor(state);
  state.veteran.bestScore = Math.max(state.veteran.bestScore, final);
  state.veteran.enabled = false;
  return final;
}

/**
 * Veteran unlocks once the player has proved the basics in normal mode —
 * it is a victory lap, not a difficulty setting a beginner stumbles into.
 */
export const VETERAN_UNLOCK_BADGES = 3;

export function shouldUnlockVeteran(state: GameState): boolean {
  return state.progression.badges.length >= VETERAN_UNLOCK_BADGES;
}
